// Section 13 justice responses. Each resolved case applies its row of the justice table:
//   Offender column  -> the offender and their household (Section 13 "a trust effect on the
//                       offender's household"); where the row names no offender trust change
//                       (restitution, imprisonment, exile, execution) the household counts as others.
//   Victims column   -> the victims and their households.
//   Others column    -> everyone else; for harsh responses (flogging, exile, execution) the cause
//                       is `watched-harsh-punishment` (Section 16: -3 to -10), otherwise `punished`.
// Trust amounts come from the justice policy's trustChange modifiers (content/policies/justice.ts).
// Every response emits one `justice` entry (the Ledger counts floggings, exiles and executions
// from it; Stream G is the only writer of state.ledger), pushes a 4-season DeterrenceEffect and
// resolves the case.
// Countdowns (restitution, custody) are ticked by the lifecycle phase at the end of this same season,
// so a sentence of n seasons is stored as n + 1 (E1-09). Exile sets `plannedDeparture` for the
// migration phase (Stream E2) to carry out next turn with the family rules of Section 14.
import { HARSH_RESPONSES, peopleSorted } from '../../core/queries';
import type {
  CaseId,
  JusticeResponse,
  JusticeRole,
  PersonId,
  PhaseContext,
  TrustCause,
} from '../../core/types';
import { killPerson } from '../shared/death';
import { applyDelta } from '../shared/deltas';
import { remember } from '../shared/memory';
import { changeTrust, type CrimeRun } from './trust';

/** Section 13: responses whose Offender column names a trust change (pardon +5, flogging -25). */
const OFFENDER_TRUST: readonly JusticeResponse[] = ['pardon', 'flogging'];

function householdOf(ctx: PhaseContext, id: PersonId): PersonId[] {
  const d = ctx.draft;
  const hid = d.people[id]?.householdId ?? d.formerPeople[id]?.householdId;
  const members = hid === undefined ? [] : (d.households[hid]?.memberIds ?? []);
  return [...new Set<PersonId>([id, ...members])].sort();
}

/** Who stands in which column of the table for this case (living people only). */
export function justiceRoles(
  ctx: PhaseContext,
  offenderId: PersonId,
  victimIds: readonly PersonId[],
  response: JusticeResponse,
): Map<PersonId, JusticeRole> {
  const roles = new Map<PersonId, JusticeRole>();
  const household = new Set(householdOf(ctx, offenderId));
  const victims = new Set<PersonId>();
  for (const v of victimIds) for (const id of householdOf(ctx, v)) victims.add(id);
  for (const p of peopleSorted(ctx.draft)) {
    if (p.id === offenderId) roles.set(p.id, 'offender');
    else if (household.has(p.id))
      roles.set(p.id, OFFENDER_TRUST.includes(response) ? 'offender' : 'other');
    else if (victims.has(p.id)) roles.set(p.id, 'victim');
    else roles.set(p.id, 'other');
  }
  return roles;
}

function sentence(existing: { seasonsRemaining: number } | null, seasons: number): number {
  // +1: the lifecycle phase ticks countdowns at the end of this season (E1-09).
  return Math.max(existing?.seasonsRemaining ?? 0, seasons + 1);
}

/** Apply a justice response to a pending case and resolve it (closing its prompt, if open). */
export function applyResponse(
  ctx: PhaseContext,
  run: CrimeRun,
  caseId: CaseId,
  response: JusticeResponse,
): void {
  const d = ctx.draft;
  const c = d.cases[caseId];
  if (c === undefined || c.status === 'resolved') return;
  if (d.openPrompts.some((p) => p.kind === 'justice' && p.caseId === caseId))
    d.openPrompts = d.openPrompts.filter((p) => p.kind !== 'justice' || p.caseId !== caseId);
  run.judged.add(c.offenderId);
  const offender = d.people[c.offenderId];
  if (offender === undefined) {
    // The offender died or left before judgement: nothing to apply (Ledger shows the fate).
    c.status = 'resolved';
    c.response = null;
    return;
  }
  const j = ctx.cfg.justice;
  const turn = ctx.season.turn;
  const harsh = HARSH_RESPONSES.includes(response);
  ctx.report.add({ kind: 'justice', caseId, offenderId: offender.id, response });

  const roles = justiceRoles(ctx, offender.id, c.victimIds, response);
  let offenderDelta = 0;
  const others: PersonId[] = [];
  for (const [id, role] of roles) {
    if (role === 'other') others.push(id);
    if (id === offender.id && response === 'execution') continue;
    const cause: TrustCause = role === 'other' && harsh ? 'watched-harsh-punishment' : 'punished';
    const delta = changeTrust(ctx, run, id, cause, { response, role });
    if (id === offender.id) offenderDelta = delta;
  }
  const othersHappiness = (amount: number, source: string): void => {
    for (const id of others)
      applyDelta(ctx, { kind: 'person', personId: id, field: 'happiness', amount, source });
  };

  switch (response) {
    case 'pardon':
      break;
    case 'restitution':
      offender.status.restitution = {
        seasonsRemaining: sentence(offender.status.restitution, j.restitution.seasons),
      };
      applyDelta(ctx, {
        kind: 'person',
        personId: offender.id,
        field: 'happiness',
        amount: j.restitution.offenderHappiness,
        source: 'justice:restitution',
      });
      break;
    case 'imprisonment': {
      const seasons = ctx.rng.int(j.imprisonment.minSeasons, j.imprisonment.maxSeasons);
      offender.status.inCustody = {
        seasonsRemaining: sentence(offender.status.inCustody, seasons),
        caseId,
      };
      break;
    }
    case 'flogging':
      applyDelta(ctx, {
        kind: 'person',
        personId: offender.id,
        field: 'strength',
        amount: j.flogging.strength,
        source: 'justice:flogging',
      });
      applyDelta(ctx, {
        kind: 'person',
        personId: offender.id,
        field: 'health',
        amount: j.flogging.health,
        source: 'justice:flogging',
      });
      othersHappiness(j.flogging.othersHappiness, 'justice:flogging');
      break;
    case 'exile':
      offender.plannedDeparture = { reason: 'exile', atTurn: turn + 1 };
      break;
    case 'execution':
      othersHappiness(j.execution.othersHappiness, 'justice:execution');
      break;
  }
  if (response !== 'execution') {
    remember(ctx, offender, { turn, kind: 'punished', amount: offenderDelta });
  }
  d.deterrence.push({
    multiplier: j[response].deterrence,
    untilTurn: turn + j.deterrenceSeasons,
    source: response,
  });
  c.response = response;
  c.status = 'resolved';
  if (response === 'execution') killPerson(ctx, offender.id, 'execution');
}
